import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Printer } from 'lucide-react';
import Modal from '../components/Modal';
import api from '../services/api';
import { formatCurrency, today } from '../utils/helpers';

const ENDPOINT = '/receipts_payments.php';

const TYPE_LABEL = { receipt: 'Receipt', payment: 'Payment' };

const num = (v) => parseFloat(v) || 0;
const errMsg = (err, fallback) => err?.response?.data?.message || fallback;
// Returns { field: message } for each missing/invalid required field
const validate = (f) => {
  const errors = {};
  if (!f.ledger_no.trim()) errors.ledger_no = 'Ledger No is required';
  if (!f.particulars.trim()) errors.particulars = 'Particulars is required';
  if (String(f.amount).trim() === '') errors.amount = 'Amount is required';
  else if (num(f.amount) <= 0) errors.amount = 'Amount must be greater than 0';
  return errors;
};

const emptyForm = () => ({ entry_type: 'receipt', ledger_no: '', particulars: '', amount: '' });

export default function ReceiptsPayments() {
  const [date, setDate] = useState(today());
  const [entries, setEntries] = useState([]);
  const [bf, setBf] = useState(0);
  const [bfSaved, setBfSaved] = useState(false);
  const [maintenance, setMaintenance] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});

  // Update a field and drop its error once it becomes valid
  const setField = (key, value) => {
    const next = { ...form, [key]: value };
    setForm(next);
    if (errors[key] && !validate(next)[key]) {
      setErrors(es => { const copy = { ...es }; delete copy[key]; return copy; });
    }
  };

  const [dayOpen, setDayOpen] = useState(false);
  const [dayForm, setDayForm] = useState({ bf_amount: '', maintenance: '' });

  const load = useCallback(() => {
    if (!date) return;
    setLoading(true);
    api.get(ENDPOINT, { params: { date } })
      .then(r => {
        const d = r.data.data || {};
        setEntries(d.entries || []);
        setBf(num(d.bf_amount));
        setBfSaved(!!d.bf_saved);
        setMaintenance(d.maintenance || '');
      })
      .catch(err => toast.error(errMsg(err, 'Failed to load receipts & payments')))
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const receipts = entries.filter(e => e.entry_type === 'receipt');
  const payments = entries.filter(e => e.entry_type === 'payment');
  const receiptsSum = receipts.reduce((s, e) => s + num(e.amount), 0);
  const totalReceipts = bf + receiptsSum;
  const totalPayments = payments.reduce((s, e) => s + num(e.amount), 0);
  const balance = totalReceipts - totalPayments;
  const rowCount = Math.max(receipts.length, payments.length);

  const openAdd = () => { setEditId(null); setForm(emptyForm()); setErrors({}); setOpen(true); };
  const openEdit = (e) => {
    setEditId(e.id);
    setErrors({});
    setForm({ entry_type: e.entry_type, ledger_no: e.ledger_no || '', particulars: e.particulars || '', amount: String(num(e.amount)) });
    setOpen(true);
  };
  const openDay = () => {
    setDayForm({ bf_amount: bf ? String(bf) : '', maintenance });
    setDayOpen(true);
  };

  const save = async () => {
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error('Please fill all required fields');
    setSaving(true);
    try {
      const body = { ...form, sheet_date: date };
      const r = editId
        ? await api.put(ENDPOINT, body, { params: { id: editId } })
        : await api.post(ENDPOINT, body);
      toast.success(r.data.message || 'Saved');
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to save entry'));
    } finally {
      setSaving(false);
    }
  };

  const saveDay = async () => {
    setSaving(true);
    try {
      await api.put(ENDPOINT, dayForm, { params: { day: date } });
      toast.success('Saved');
      setDayOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.delete(ENDPOINT, { params: { id } });
      toast.success('Entry deleted');
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to delete entry'));
    }
  };

  const actions = (e) => (
    <td className="no-print rp-act">
      <div className="ds-row-actions">
        <button className="btn btn-gray btn-sm" onClick={() => openEdit(e)} aria-label="Edit entry"><Pencil size={12} /></button>
        <button className="btn btn-red btn-sm" onClick={() => remove(e.id)} aria-label="Delete entry"><Trash2 size={12} /></button>
      </div>
    </td>
  );

  const side = (e, split) => e ? (
    <>
      <td className={split ? 'rp-split' : ''}>{e.ledger_no}</td>
      <td className="rp-part">{e.particulars}</td>
      <td className="ds-num">{formatCurrency(e.amount)}</td>
      {actions(e)}
    </>
  ) : (
    <><td className={split ? 'rp-split' : ''}></td><td></td><td></td><td className="no-print"></td></>
  );

  return (
    <div className="card ds-card rp-card">
      <div className="ds-head">
        <div className="ds-date">
          <label htmlFor="rp-date">Date</label>
          <input id="rp-date" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <h3 className="ds-title">RECEIPTS &amp; PAYMENTS</h3>
        <div className="ds-actions no-print">
          <button className="btn btn-gray" onClick={() => window.print()}><Printer size={14} /> Print</button>
          <button className="btn btn-blue" onClick={openAdd}><Plus size={14} /> Add Entry</button>
        </div>
      </div>

      <div className="table-wrap">
        <table className="ds-table rp-table">
          <thead>
            <tr>
              <th colSpan={4} className="rp-side-title">RECEIPTS</th>
              <th colSpan={4} className="rp-side-title rp-split">PAYMENTS</th>
            </tr>
            <tr>
              <th>Ledger No</th>
              <th>Particulars</th>
              <th>Amount</th>
              <th className="no-print rp-act"></th>
              <th className="rp-split">Ledger No</th>
              <th>Particulars</th>
              <th>Amount</th>
              <th className="no-print rp-act"></th>
            </tr>
          </thead>
          <tbody>
            <tr className="rp-bf">
              <td></td>
              <td className="rp-part"><strong>BF</strong>{!bfSaved && !loading && <span className="rp-hint"> (from previous day)</span>}</td>
              <td className="ds-num"><strong>{formatCurrency(bf)}</strong></td>
              <td className="no-print rp-act">
                <button className="btn btn-gray btn-sm" onClick={openDay} aria-label="Edit BF"><Pencil size={12} /></button>
              </td>
              <td className="rp-split"></td><td></td><td></td><td className="no-print"></td>
            </tr>
            {loading ? (
              <tr><td colSpan={8} className="empty">Loading...</td></tr>
            ) : rowCount === 0 ? (
              <tr><td colSpan={8} className="empty">No receipts or payments for this day yet.</td></tr>
            ) : Array.from({ length: rowCount }, (_, i) => (
              <tr key={i}>
                {side(receipts[i])}
                {side(payments[i], true)}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="rp-total">
              <td></td>
              <td className="rp-part">TOTAL RECEIPTS</td>
              <td className="ds-num">{formatCurrency(totalReceipts)}</td>
              <td className="no-print"></td>
              <td className="rp-split" colSpan={2}></td>
              <td></td>
              <td className="no-print"></td>
            </tr>
            <tr className="rp-total">
              <td></td>
              <td className="rp-part">TOTAL PAYMENTS</td>
              <td className="ds-num">{formatCurrency(totalPayments)}</td>
              <td className="no-print"></td>
              <td className="rp-split" colSpan={2}></td>
              <td></td>
              <td className="no-print"></td>
            </tr>
            <tr className="ds-grand">
              <td></td>
              <td className="ds-grand-label">Balance</td>
              <td className="ds-num">{formatCurrency(balance)}</td>
              <td className="no-print"></td>
              <td className="rp-split ds-grand-label" colSpan={2}>Total</td>
              <td className="ds-num">{formatCurrency(totalPayments)}</td>
              <td className="no-print"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)}
        title={editId ? 'Edit ' + TYPE_LABEL[form.entry_type] : 'Add Entry'}
        footer={<>
          <button className="btn btn-gray" onClick={() => setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save} disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Add'}</button>
        </>}>
        <div className="form-grid">
          <div className="form-group">
            <label>Type</label>
            <select value={form.entry_type} onChange={e => setField('entry_type', e.target.value)}>
              <option value="receipt">Receipt</option>
              <option value="payment">Payment</option>
            </select>
          </div>
          <div className="form-group">
            <label>Ledger No *</label>
            <input value={form.ledger_no} placeholder="e.g. 665" className={errors.ledger_no ? 'ds-invalid' : ''}
              onChange={e => setField('ledger_no', e.target.value)} />
            {errors.ledger_no && <div className="ds-error">{errors.ledger_no}</div>}
          </div>
          <div className="form-group full-width">
            <label>Particulars *</label>
            <input value={form.particulars} placeholder="e.g. Cash received from party / Diesel payment" className={errors.particulars ? 'ds-invalid' : ''}
              onChange={e => setField('particulars', e.target.value)} />
            {errors.particulars && <div className="ds-error">{errors.particulars}</div>}
          </div>
          <div className="form-group">
            <label>Amount *</label>
            <input type="number" value={form.amount} placeholder="0.00" className={errors.amount ? 'ds-invalid' : ''}
              onChange={e => setField('amount', e.target.value)} />
            {errors.amount && <div className="ds-error">{errors.amount}</div>}
          </div>
        </div>
      </Modal>

      <Modal open={dayOpen} onClose={() => setDayOpen(false)} title="Edit BF"
        footer={<>
          <button className="btn btn-gray" onClick={() => setDayOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={saveDay} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </>}>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>BF (Brought Forward) Amount</label>
            <input type="number" value={dayForm.bf_amount} placeholder="0.00" onChange={e => setDayForm({ ...dayForm, bf_amount: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
